// src/hooks/useConfirmation.ts
import { useCallback } from 'react';
import { ProjectManagementControls } from './useProjectManagement';
import { MainView, ChapterDetailEdits } from '../types';

export interface ConfirmationLogic {
    confirmDiscardChapterDetailChanges: (actionName?: string) => boolean;
    confirmOverallDiscardChanges: (actionName?: string) => boolean;
}

export const useConfirmation = (
    projectManagement: Pick<ProjectManagementControls, 'confirmDiscardProjectChanges'>,
    areChapterDetailsCurrentlyUnsaved: () => boolean,
    isInitialBlueprintReady: boolean,
    mainView: MainView,
    activeChapterDetailEdits: Partial<ChapterDetailEdits> | null // To check if details form is active
): ConfirmationLogic => {

    const confirmDiscardChapterDetailChanges = useCallback((actionName: string = "leave this page"): boolean => {
        if (isInitialBlueprintReady && mainView === 'chaptersBuilding' && activeChapterDetailEdits && areChapterDetailsCurrentlyUnsaved()) {
            const verb = actionName.startsWith("switch to") || actionName.startsWith("load project") || actionName.startsWith("start a new project") || actionName.startsWith("open chapter merge tool") || actionName.startsWith("import a new project") ? "continue with" : "perform";
            const message = `You have unsaved changes in the current chapter's details or vitals. Discard these changes and ${verb} ${actionName}?

(Press OK to Discard & Continue, or Cancel to Stay).`;
            return window.confirm(message);
        }
        return true;
    }, [areChapterDetailsCurrentlyUnsaved, isInitialBlueprintReady, mainView, activeChapterDetailEdits]);

    const confirmOverallDiscardChanges = useCallback((actionName: string = "perform this action"): boolean => {
        if (!projectManagement.confirmDiscardProjectChanges(actionName)) {
            return false;
        }
        if (!confirmDiscardChapterDetailChanges(actionName)) {
            return false;
        }
        return true;
    }, [projectManagement.confirmDiscardProjectChanges, confirmDiscardChapterDetailChanges]);

    return {
        confirmDiscardChapterDetailChanges,
        confirmOverallDiscardChanges,
    };
};
